/**
 * Roster rules — how many players a team may have on the board.
 *
 * Every rule here is generic. The pack says how many players are allowed and
 * which role ids do not count; this module only compares strings. That split is
 * deliberate and enforced: `test/architecture.test.ts` fails the build if a
 * sport's vocabulary appears anywhere in `core`, so a rule of the form "the
 * goalkeeper is exempt" has to arrive as data.
 *
 * Adding and removing route through `addEntity` / `removeEntity` in
 * `document.ts`, which already write to and strip from every keyframe. A
 * multi-phase play therefore stays coherent with no timeline code here.
 */

import type { Entity, PlayDocument } from './document.ts';
import type { Vec2 } from './geometry.ts';
import type { Id } from './ids.ts';
import type { FormationSlot, SportPack } from './sport.ts';

/** Used when a pack declares no roster rule to name its player type. */
const DEFAULT_PLAYER_TYPE = 'player';

/** Every player on a team, exempt roles included. */
export function teamPlayers(doc: PlayDocument, pack: SportPack, teamId: string): readonly Entity[] {
  const typeId = pack.roster?.playerTypeId ?? DEFAULT_PLAYER_TYPE;
  return doc.entities.filter((entity) => entity.teamId === teamId && entity.typeId === typeId);
}

/**
 * Players on a team that count toward its cap.
 *
 * A player carrying no role counts: an exemption has to be claimed by naming an
 * exempt role, never granted by omission.
 */
export function rosterCount(doc: PlayDocument, pack: SportPack, teamId: string): number {
  const exempt = exemptRoleIds(pack);
  return teamPlayers(doc, pack, teamId).filter(
    (entity) => entity.roleId === undefined || !exempt.includes(entity.roleId),
  ).length;
}

function exemptRoleIds(pack: SportPack): readonly string[] {
  return (pack.roster?.exemptRoles ?? []).map((exempt) => exempt.roleId);
}

/** How many players of one role a team holds. */
function roleCount(doc: PlayDocument, pack: SportPack, teamId: string, roleId: string): number {
  return teamPlayers(doc, pack, teamId).filter((entity) => entity.roleId === roleId).length;
}

/**
 * Whether a role is one the team may hold no more of.
 *
 * Distinct from "cannot add": a role can be unavailable because the team is at
 * the cap, which is a different fact and has a different remedy. Only a role
 * carrying its own maximum can be at that maximum, so this stays false for
 * every role bounded by the cap alone.
 */
export function isRoleAtMax(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
  roleId: string,
): boolean {
  const exempt = pack.roster?.exemptRoles.find((candidate) => candidate.roleId === roleId);
  return exempt !== undefined && roleCount(doc, pack, teamId, exempt.roleId) >= exempt.max;
}

/**
 * Whether the team may take another player, of `roleId` when one is named.
 *
 * An exempt role is bounded by its own maximum and nothing else — it does not
 * count toward the cap, so the cap cannot bound it. Every other role, and no
 * role at all, is bounded by the cap. Without the role, the question is only
 * about the cap, which is what a caller adding an unspecified player asks.
 */
export function canAddPlayer(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
  roleId?: string,
): boolean {
  const rule = pack.roster;
  if (!rule) return false;
  // With a lineup, an unspecified player is the next one in it.
  if (roleId === undefined && lineupOf(pack, teamId).length > 0) return nextLineupPlayer(doc, pack, teamId) !== undefined;
  if (roleId !== undefined && isRoleAtMax(doc, pack, teamId, roleId)) return false;
  if (roleId !== undefined && exemptRoleIds(pack).includes(roleId)) return true;
  return rosterCount(doc, pack, teamId) < rule.maxPerTeam;
}

/**
 * Whether `−` would take anyone off. The floor is empty, not one: a team may be
 * cleared completely.
 *
 * Defined as "playerToRemove finds someone" so the button's enabled state can
 * never disagree with what pressing it does.
 */
export function canRemovePlayer(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
  focusId: Id | null,
  roleId?: string,
): boolean {
  if (!pack.roster) return false;
  return playerToRemove(doc, pack, teamId, focusId, roleId) !== undefined;
}

/** Reads a player's label as a number, or -1 when it is not one. */
function labelNumber(label: string): number {
  const parsed = Number.parseInt(label, 10);
  return Number.isFinite(parsed) ? parsed : -1;
}

/**
 * The lowest positive integer the team is not already using.
 *
 * Filling gaps rather than appending keeps the numbering tight after a removal,
 * which is what a coach expects when they take 4 off and add another player.
 */
export function nextPlayerNumber(doc: PlayDocument, pack: SportPack, teamId: string): string {
  const used = new Set(teamPlayers(doc, pack, teamId).map((entity) => entity.label));
  // At most `used.size` numbers are taken, so one of 1..size+1 is always free.
  for (let candidate = 1; candidate <= used.size + 1; candidate += 1) {
    if (!used.has(String(candidate))) return String(candidate);
  }
  return String(used.size + 1);
}

export function isPlayerNumberFree(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
  label: string,
): boolean {
  return !teamPlayers(doc, pack, teamId).some((entity) => entity.label === label);
}

/**
 * Which player the remove control takes off.
 *
 * The selection wins whenever it is one of this team's players, whatever its
 * role: clicking a player states intent more directly than a dropdown does, so
 * a chosen role must not override it. With nothing selected the role narrows
 * the field and the highest number within it goes; with no role, the highest
 * number of all. Naming a role the team does not hold removes nobody rather
 * than falling back to someone else, which is what made the control confusing.
 */
export function playerToRemove(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
  focusId: Id | null,
  roleId?: string,
): Id | undefined {
  const players = teamPlayers(doc, pack, teamId);
  const selected = players.find((entity) => entity.id === focusId);
  if (selected) return selected.id;

  // With a lineup and no role named, the team empties in reverse fill order:
  // anyone outside the lineup first, highest number first, then the lineup
  // from its end. Adding everyone back then returns the lineup exactly.
  if (roleId === undefined && lineupOf(pack, teamId).length > 0) {
    const { filled, extras } = assignLineup(doc, pack, teamId);
    const extra = [...extras].sort((a, b) => labelNumber(b.label) - labelNumber(a.label))[0];
    if (extra) return extra.id;
    for (let i = filled.length - 1; i >= 0; i -= 1) {
      if (filled[i]) return filled[i]!.id;
    }
    return undefined;
  }

  const candidates =
    roleId === undefined ? players : players.filter((entity) => entity.roleId === roleId);

  let highest: Entity | undefined;
  for (const candidate of candidates) {
    // >= so a tie falls to the later entity, which is the more recently added.
    if (!highest || labelNumber(candidate.label) >= labelNumber(highest.label)) highest = candidate;
  }
  return highest?.id;
}

/** Lateral positions a new player walks through, so repeated adds fan out. */
const SPAWN_LADDER = [0.5, 0.32, 0.68, 0.2, 0.8, 0.14, 0.86];

/**
 * Where a new player lands: in the half its team defends, clear of the
 * opposition's set. `addEntity` clamps to the field, so this needs no bounds
 * check of its own.
 */
export function spawnPosition(doc: PlayDocument, pack: SportPack, teamId: string): Vec2 {
  const team = pack.teams.find((candidate) => candidate.id === teamId);
  const defendsLeft = team?.attacking === 'right';
  const index = teamPlayers(doc, pack, teamId).length % SPAWN_LADDER.length;
  return { x: defendsLeft ? 0.15 : 0.85, y: SPAWN_LADDER[index]! };
}

/* ------------------------------------------------------------------ */
/* The standard lineup                                                 */
/* ------------------------------------------------------------------ */

function lineupOf(pack: SportPack, teamId: string): readonly FormationSlot[] {
  return (pack.roster?.lineup ?? []).filter((slot) => slot.teamId === teamId);
}

/**
 * Matches a team's players to its lineup slots, in fill order: each slot takes
 * an unmatched player of its role, the one wearing the slot's number if there
 * is one, otherwise the lowest number. Players left over are `extras`.
 */
function assignLineup(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
): { filled: readonly (Entity | undefined)[]; extras: readonly Entity[] } {
  const unmatched = [...teamPlayers(doc, pack, teamId)];
  const filled = lineupOf(pack, teamId).map((slot) => {
    const candidates = unmatched.filter((entity) => entity.roleId === slot.roleId);
    const chosen =
      candidates.find((entity) => entity.label === slot.label) ??
      [...candidates].sort((a, b) => labelNumber(a.label) - labelNumber(b.label))[0];
    if (chosen) unmatched.splice(unmatched.indexOf(chosen), 1);
    return chosen;
  });
  return { filled, extras: unmatched };
}

/**
 * The player `+` adds next: the first lineup slot the team has not filled,
 * with the slot's number if it is free and the lowest free number otherwise.
 * Nothing when every slot is filled or the team is at its cap.
 */
export function nextLineupPlayer(
  doc: PlayDocument,
  pack: SportPack,
  teamId: string,
): { readonly roleId: string; readonly label: string; readonly at: Vec2 } | undefined {
  const slots = lineupOf(pack, teamId);
  const { filled } = assignLineup(doc, pack, teamId);
  const slot = slots.find((_, i) => filled[i] === undefined);
  if (!slot || !canAddPlayer(doc, pack, teamId, slot.roleId)) return undefined;
  const label = isPlayerNumberFree(doc, pack, teamId, slot.label) ? slot.label : nextPlayerNumber(doc, pack, teamId);
  return { roleId: slot.roleId, label, at: slot.at };
}
